import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import type { ApidaeTouristicObject } from '@alterrego/module-apidae';
import { Badge } from '@alterrego/core-ui';
import './ExplorerScreen.css';

// Fix Leaflet default marker icons (broken in webpack/vite)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const BACKEND_URL = 'https://api.apps.alterrego.com';
const FAVORITES_KEY = 'cchmv_explorer_favorites';
const CENTER: [number, number] = [45.21, 6.90];
const ZOOM = 10;

type ViewMode = 'list' | 'map';

type FilterType = 'poi' | 'activite' | 'restaurant' | 'hebergement';

const TYPE_CONFIG: Record<FilterType, { label: string; icon: string; endpoint: string }> = {
  poi: { label: 'Points d\'intérêt', icon: '🏛', endpoint: '/apidae/pois' },
  activite: { label: 'Activités', icon: '🧗', endpoint: '/apidae/activities' },
  restaurant: { label: 'Restaurants', icon: '🍽', endpoint: '/apidae/restaurants' },
  hebergement: { label: 'Hébergements', icon: '🏨', endpoint: '/apidae/accommodation' },
};

const COMMUNES = [
  { code: '73023', nom: 'Avrieux' },
  { code: '73024', nom: 'Bessans' },
  { code: '73031', nom: 'Bonneval-sur-Arc' },
  { code: '73032', nom: 'Bramans' },
  { code: '73043', nom: 'Chamonix' },
  { code: '73046', nom: 'Lanslebourg' },
  { code: '73147', nom: 'Modane' },
  { code: '73148', nom: 'Sollières-Sardières' },
  { code: '73167', nom: 'Termignon' },
  { code: '73173', nom: 'Tignes' },
  { code: '73219', nom: 'Val-Cenis' },
  { code: '73274', nom: 'Val d\'Isère' },
  { code: '73275', nom: 'Villarodin-Bourget' },
];

function loadFavorites(): Set<number> {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveFavorites(favs: Set<number>) {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify([...favs]));
}

function poiIcon(type: FilterType): string {
  return TYPE_CONFIG[type].icon;
}

function getPoiType(poi: ApidaeTouristicObject): FilterType {
  const t = poi.type;
  if (!t) return 'poi';
  if (t === 'ACTIVITE') return 'activite';
  if (t === 'RESTAURATION') return 'restaurant';
  if (['HEBERGEMENT_COLLECTIF', 'HEBERGEMENT_LOCATIF', 'HOTELLERIE', 'HOTELLERIE_PLEIN_AIR'].includes(t)) return 'hebergement';
  return 'poi';
}

function POICard({
  poi,
  isFav,
  onFavToggle,
  onTap,
}: {
  poi: ApidaeTouristicObject;
  isFav: boolean;
  onFavToggle: (id: number) => void;
  onTap: (poi: ApidaeTouristicObject) => void;
}) {
  const name = poi.nom?.libelleFr ?? 'Point d\'intérêt';
  const commune = poi.localisation?.adresse?.commune?.nom;
  const img = poi.illustrations?.[0]?.traductionFichiers?.[0]?.urlFiche;
  const desc = poi.presentation?.descriptifCourt?.libelleFr;
  const type = getPoiType(poi);

  return (
    <div className="cchmv-poi-card" onClick={() => onTap(poi)}>
      {img
        ? <img className="cchmv-poi-img" src={img} alt={name} loading="lazy" />
        : <div className="cchmv-poi-img-placeholder">{poiIcon(type)}</div>
      }
      <div className="cchmv-poi-body">
        <Badge variant="primary">{TYPE_CONFIG[type].label}</Badge>
        <p className="cchmv-poi-name">{name}</p>
        {commune && <p className="cchmv-poi-commune">📍 {commune}</p>}
        {desc && <p className="cchmv-poi-desc">{desc}</p>}
      </div>
      <div className="cchmv-poi-card-actions">
        <button
          className={`cchmv-fav-btn${isFav ? ' active' : ''}`}
          onClick={(e) => { e.stopPropagation(); onFavToggle(poi.id); }}
          aria-label={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
        >
          {isFav ? '♥' : '♡'}
        </button>
      </div>
    </div>
  );
}

function POIDetail({
  poi,
  isFav,
  onFavToggle,
  onClose,
}: {
  poi: ApidaeTouristicObject;
  isFav: boolean;
  onFavToggle: (id: number) => void;
  onClose: () => void;
}) {
  const name = poi.nom?.libelleFr ?? 'Point d\'intérêt';
  const commune = poi.localisation?.adresse?.commune?.nom;
  const img = poi.illustrations?.[0]?.traductionFichiers?.[0]?.urlFiche;
  const desc = poi.presentation?.descriptifCourt?.libelleFr
    ?? poi.presentation?.descriptifDetaille?.libelleFr;
  const type = getPoiType(poi);

  return (
    <div className="cchmv-detail-overlay" onClick={onClose}>
      <div className="cchmv-detail-sheet" onClick={(e) => e.stopPropagation()}>
        {img
          ? <img className="cchmv-detail-img" src={img} alt={name} />
          : <div className="cchmv-detail-img-placeholder">{poiIcon(type)}</div>
        }
        <div className="cchmv-detail-content">
          <div className="cchmv-detail-header">
            <h2 className="cchmv-detail-name">{name}</h2>
            <button className="cchmv-detail-close" onClick={onClose}>✕</button>
          </div>
          <div className="cchmv-detail-meta">
            <Badge variant="primary">{TYPE_CONFIG[type].label}</Badge>
            {commune && <span className="cchmv-detail-commune">📍 {commune}</span>}
            <button
              className={`cchmv-fav-btn${isFav ? ' active' : ''}`}
              onClick={() => onFavToggle(poi.id)}
              style={{ marginLeft: 'auto' }}
            >
              {isFav ? '♥' : '♡'}
            </button>
          </div>
          {desc && <p className="cchmv-detail-desc">{desc}</p>}
        </div>
      </div>
    </div>
  );
}

function FiltersSheet({
  activeTypes,
  activeCommunes,
  onApply,
  onClose,
}: {
  activeTypes: Set<FilterType>;
  activeCommunes: Set<string>;
  onApply: (types: Set<FilterType>, communes: Set<string>) => void;
  onClose: () => void;
}) {
  const [types, setTypes] = useState(new Set(activeTypes));
  const [communes, setCommunes] = useState(new Set(activeCommunes));

  function toggleType(t: FilterType) {
    setTypes((prev) => {
      const next = new Set(prev);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });
  }

  function toggleCommune(code: string) {
    setCommunes((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  }

  return (
    <div className="cchmv-filters-overlay" onClick={onClose}>
      <div className="cchmv-filters-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="cchmv-filters-handle" />
        <p className="cchmv-filters-title">Filtres</p>

        <div className="cchmv-filters-section">
          <p className="cchmv-filters-section-label">Type</p>
          <div className="cchmv-filter-chips">
            {(Object.keys(TYPE_CONFIG) as FilterType[]).map((t) => (
              <button
                key={t}
                className={`cchmv-chip${types.has(t) ? ' active' : ''}`}
                onClick={() => toggleType(t)}
              >
                {TYPE_CONFIG[t].icon} {TYPE_CONFIG[t].label}
              </button>
            ))}
          </div>
        </div>

        <div className="cchmv-filters-section">
          <p className="cchmv-filters-section-label">Commune</p>
          <div className="cchmv-filter-chips">
            {COMMUNES.map((c) => (
              <button
                key={c.code}
                className={`cchmv-chip${communes.has(c.code) ? ' active' : ''}`}
                onClick={() => toggleCommune(c.code)}
              >
                {c.nom}
              </button>
            ))}
          </div>
        </div>

        <div className="cchmv-filters-footer">
          <button
            className="cchmv-filters-reset"
            onClick={() => { setTypes(new Set()); setCommunes(new Set()); }}
          >
            Réinitialiser
          </button>
          <button className="cchmv-filters-apply" onClick={() => onApply(types, communes)}>
            Appliquer
          </button>
        </div>
      </div>
    </div>
  );
}

export function ExplorerScreen() {
  const navigate = useNavigate();
  const [view, setView] = useState<ViewMode>('list');
  const [pois, setPois] = useState<ApidaeTouristicObject[]>([]);
  const [numFound, setNumFound] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [favorites, setFavorites] = useState<Set<number>>(loadFavorites);
  const [selectedPoi, setSelectedPoi] = useState<ApidaeTouristicObject | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTypes, setActiveTypes] = useState<Set<FilterType>>(new Set());
  const [activeCommunes, setActiveCommunes] = useState<Set<string>>(new Set());

  const fetchPOIs = useCallback(async () => {
    setLoading(true);
    setError(null);

    const endpoints: string[] = activeTypes.size === 0
      ? ['/apidae/pois', '/apidae/activities', '/apidae/restaurants', '/apidae/accommodation']
      : [...activeTypes].map((t) => TYPE_CONFIG[t].endpoint);

    const communeParam = activeCommunes.size > 0
      ? `&communes=${[...activeCommunes].join(',')}`
      : '';

    try {
      const results = await Promise.all(
        endpoints.map((ep) =>
          fetch(`${BACKEND_URL}${ep}?count=50&first=0${communeParam}`)
            .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        )
      );
      const all: ApidaeTouristicObject[] = results.flatMap((r) => r.items ?? []);
      const total: number = results.reduce((acc, r) => acc + (r.numFound ?? 0), 0);
      setPois(all);
      setNumFound(total);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeTypes, activeCommunes]);

  useEffect(() => { fetchPOIs(); }, [fetchPOIs]);

  const filtered = useMemo(() => {
    if (!search.trim()) return pois;
    const q = search.toLowerCase();
    return pois.filter((p) => {
      const name = (p.nom?.libelleFr ?? '').toLowerCase();
      const commune = (p.localisation?.adresse?.commune?.nom ?? '').toLowerCase();
      return name.includes(q) || commune.includes(q);
    });
  }, [pois, search]);

  function toggleFav(id: number) {
    setFavorites((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      saveFavorites(next);
      return next;
    });
  }

  const hasFilter = activeTypes.size > 0 || activeCommunes.size > 0;

  const poisWithCoords = useMemo(
    () => filtered.filter((p) => {
      const geo = p.localisation?.geolocalisation;
      return geo?.valide && geo.geoJson?.coordinates;
    }),
    [filtered]
  );

  return (
    <div className="cchmv-explorer">
      <div className="cchmv-explorer-header">
        <div className="cchmv-explorer-header-row">
          <button className="cchmv-explorer-back" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}>‹</button>
          <h1 className="cchmv-explorer-title">Explorer</h1>
          <span className="cchmv-explorer-count">
            {loading ? '…' : `${filtered.length} résultat${filtered.length > 1 ? 's' : ''}`}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="cchmv-view-toggle">
            <button
              className={`cchmv-toggle-btn${view === 'list' ? ' active' : ''}`}
              onClick={() => setView('list')}
            >
              ☰ Liste
            </button>
            <button
              className={`cchmv-toggle-btn${view === 'map' ? ' active' : ''}`}
              onClick={() => setView('map')}
            >
              🗺 Carte
            </button>
          </div>
        </div>

        <div className="cchmv-search-row">
          <div className="cchmv-search-wrap">
            <span className="cchmv-search-icon">🔍</span>
            <input
              className="cchmv-search-input"
              placeholder="Rechercher un lieu, une commune…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            className={`cchmv-filter-btn${hasFilter ? ' has-filter' : ''}`}
            onClick={() => setShowFilters(true)}
          >
            ⚙️
          </button>
        </div>
      </div>

      {loading && (
        <div className="cchmv-status-center">
          <span className="cchmv-status-icon">🔍</span>
          <span>Chargement des points d'intérêt…</span>
        </div>
      )}

      {!loading && error && (
        <div className="cchmv-status-center">
          <span className="cchmv-status-icon">⚠️</span>
          <span>Erreur : {error}</span>
          <button
            onClick={fetchPOIs}
            style={{ marginTop: 8, padding: '8px 16px', borderRadius: 10, border: '1.5px solid #e8edf2', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            Réessayer
          </button>
        </div>
      )}

      {!loading && !error && view === 'list' && (
        <div className="cchmv-list-container">
          {filtered.length === 0 && (
            <div className="cchmv-status-center" style={{ flex: 'none', marginTop: 40 }}>
              <span className="cchmv-status-icon">🧭</span>
              <span>Aucun résultat pour cette recherche</span>
            </div>
          )}
          {filtered.map((poi) => (
            <POICard
              key={poi.id}
              poi={poi}
              isFav={favorites.has(poi.id)}
              onFavToggle={toggleFav}
              onTap={setSelectedPoi}
            />
          ))}
        </div>
      )}

      {!loading && !error && view === 'map' && (
        <div className="cchmv-map-container">
          <MapContainer center={CENTER} zoom={ZOOM} style={{ width: '100%', height: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {poisWithCoords.map((poi) => {
              const coords = poi.localisation!.geolocalisation!.geoJson!.coordinates as [number, number];
              return (
                <Marker key={poi.id} position={[coords[1], coords[0]]} icon={redIcon}>
                  <Popup className="cchmv-map-popup">
                    <p className="cchmv-popup-name">{poi.nom?.libelleFr ?? 'Point d\'intérêt'}</p>
                    {poi.localisation?.adresse?.commune?.nom && (
                      <p className="cchmv-popup-commune">📍 {poi.localisation.adresse.commune.nom}</p>
                    )}
                    <a
                      className="cchmv-popup-link"
                      href="#"
                      onClick={(e) => { e.preventDefault(); setSelectedPoi(poi); }}
                    >
                      Voir la fiche →
                    </a>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>
      )}

      {showFilters && (
        <FiltersSheet
          activeTypes={activeTypes}
          activeCommunes={activeCommunes}
          onApply={(types, communes) => {
            setActiveTypes(types);
            setActiveCommunes(communes);
            setShowFilters(false);
          }}
          onClose={() => setShowFilters(false)}
        />
      )}

      {selectedPoi && (
        <POIDetail
          poi={selectedPoi}
          isFav={favorites.has(selectedPoi.id)}
          onFavToggle={toggleFav}
          onClose={() => setSelectedPoi(null)}
        />
      )}
    </div>
  );
}
